import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Spinner } from 'flowbite-react';
import { HiMenu } from 'react-icons/hi';
import { Book, ChevronUp } from 'lucide-react';
import LoadingAnimation from '../LoadingAnimation';
import { useDarkMode } from '../../contexts/DarkModeContext';
import { AnimatePresence, motion } from 'framer-motion';
import { useReadingProgress } from '../../hooks/useReadingProgress';
import { useSettings } from '../../contexts/SettingsContext';
import { loadFontsForPages } from '../../utils/fontLoader';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

// Import components
import QuranSidebar from './components/QuranSidebar';
import AudioPlayer from './components/AudioPlayer';
import MobilePlayer from './components/MobilePlayer';
import VerseDisplay from './components/VerseDisplay';
import NoteModal from './components/NoteModal';
import QuranNavigationPage from './components/QuranNavigationPage';
import QuranSettingsWrapper from '../settings/QuranSettingsWrapper';
import QuranHeader from './components/QuranHeader';
import QuranSettings from '../Quran/components/QuranSettings';
import NoteSheet from './components/NoteSheet';
import SkeletonVerse from './components/SkeletonVerse';
import ArabicOnlyDisplay from './components/ArabicOnlyDisplay';

// Import hooks
import useGaplessQuranAudio from './hooks/useGaplessQuranAudio';
import useQuranData from './hooks/useQuranData';
import useQuranNotes from './hooks/useQuranNotes';
import useQuranMemorization from './hooks/useQuranMemorization';
import useVerseReadTracking from '../../hooks/useVerseReadTracking';
import useEnhancedScroll from './hooks/useEnhancedScroll';

const logRender = (componentName, props) => {
  // Remove this function or comment it out
  // console.log(`${componentName} rendered`, props);
};

// Add debounce utility at the top level
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Consistent loading state for Quran: show 10 SkeletonVerse components
function LoadingState({ isDarkMode }) {
  return (
    <div className="space-y-4 py-8">
      {Array.from({ length: 10 }).map((_, i) => (
        <SkeletonVerse key={i} isDarkMode={isDarkMode} />
      ))}
    </div>
  );
}

// Add this before the Quran component
const SCROLL_RETRY_COUNT = 3;
const SCROLL_RETRY_DELAY = 500;

function Quran() {
  // Remove these console.log statements
  // console.log('Quran component rendered');

  // Core hooks and state
  const { settings, updateSettings, isInitialized: isSettingsInitialized } = useSettings();
  const { isDarkMode } = useDarkMode();
  const { markVerseAsRead, getReadingProgress } = useReadingProgress();
  const location = useLocation();
  const quranData = useQuranData();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // All refs
  const observerTarget = useRef(null);
  const footnoteRef = useRef(null);
  const verseRefs = useRef({});
  const scrollContainerRef = useRef(null);
  const contentContainerRef = useRef(null);
  const observedVerses = useRef(new Set());
  const isObserverSetup = useRef(false);
  const topObserverTarget = useRef(null);
  const previousSurahRef = useRef(null);

  // All memoized values
  const quranSettings = useMemo(() => {
    return settings?.quran || {};
  }, [settings?.quran]);
  
  const isReadingMode = useMemo(() => {
    return quranSettings.mode === 'reading';
  }, [quranSettings.mode]);

  // All state
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 1024);
  const [activeFootnote, setActiveFootnote] = useState(null);
  const [repeatingVerse, setRepeatingVerse] = useState(null);
  const [showNavigation, setShowNavigation] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isInternalNavigation, setIsInternalNavigation] = useState(undefined);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [navigationHistory, setNavigationHistory] = useState({
    previousPage: null,
    currentPage: 'quran'
  });
  const [showNoteSheet, setShowNoteSheet] = useState(false);
  const [currentNoteVerse, setCurrentNoteVerse] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showGuestBanner, setShowGuestBanner] = useState(() => {
    return sessionStorage.getItem('hideQuranGuestBanner') !== '1';
  });

  // Add detection for mobile/tablet portrait mode
  const [isMobileOrTabletPortrait, setIsMobileOrTabletPortrait] = useState(window.innerWidth < 1024);
  
  // Update on resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobileOrTabletPortrait(window.innerWidth < 1024);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Add this callback before calling useGaplessQuranAudio
  const handleNativeStateSync = useCallback(({ surahId, reciterId, verseIndex }) => {
    // Only update if the state is different
    if (
      surahId &&
      (quranData.selectedSurah !== surahId ||
        quranData.selectedReciter !== reciterId ||
        quranData.currentVerse !== verseIndex)
    ) {
      if (quranData.selectedSurah !== surahId) {
        quranData.loadSurahContent(surahId, `${surahId}:${verseIndex + 1}`);
      } else {
        if (quranData.selectedReciter !== reciterId && quranData.setSelectedReciter) {
          quranData.setSelectedReciter(reciterId);
        }
        if (quranData.currentVerse !== verseIndex && quranData.setCurrentVerse) {
          quranData.setCurrentVerse(verseIndex);
        }
      }
    }
  }, [quranData.selectedSurah, quranData.selectedReciter, quranData.currentVerse, quranData.loadSurahContent, quranData.setSelectedReciter, quranData.setCurrentVerse]);

  // Compute surahName for audio/lockscreen
  const surahName = quranData.surahs && quranData.selectedSurah
    ? quranData.surahs.find(s => s.id.toString() === quranData.selectedSurah)?.name_simple
    : undefined;
  // Compute reciterName for audio/lockscreen
  const reciterName = quranData.reciters && quranData.selectedReciter
    ? quranData.reciters.find(r => r.id.toString() === quranData.selectedReciter)?.reciter_name
    : undefined;

  // Replace useGaplessQuranAudio call
  const quranAudio = useGaplessQuranAudio(
    quranData.selectedSurah,
    quranData.selectedReciter,
    quranData.currentVerse,
    quranData.setCurrentVerse,
    surahName, // pass surahName
    reciterName, // pass reciterName
    quranData.markVerseAsRead,
    handleNativeStateSync
  );

  // Add error state for audio
  const [audioError, setAudioError] = useState(null);

  // Add centralized verse read tracking using the useVerseReadTracking hook
  const { readingState } = useMemo(() => {
    return getReadingProgress() || {};
  }, [getReadingProgress]);

  // Safely get the isRead object with null checks
  const safeIsRead = useMemo(() => {
    try {
      if (!readingState || !quranData.selectedSurah) {
        return {};
      }
      return readingState[quranData.selectedSurah] || {};
    } catch (error) {
      console.error('Error accessing reading state:', error);
      return {};
    }
  }, [readingState, quranData.selectedSurah]);

  // Use centralized tracking at the parent level with proper error handling
  useVerseReadTracking({
    key: quranData.selectedSurah, // force reset on surah change
    containerRef: contentContainerRef,
    verses: quranData.surahContent || [],
    isRead: safeIsRead,
    onVerseRead: (verseNum) => {
      if (!quranData.selectedSurah) {
        console.warn('Attempted to mark verse as read with missing surah context:', verseNum);
        return;
      }
      if (typeof verseNum === 'string' && verseNum.includes(':')) {
        // Already full key
        const [surahId, vNum] = verseNum.split(':');
        markVerseAsRead(surahId, verseNum);
      } else {
        // Compose full key
        const verseKey = `${quranData.selectedSurah}:${verseNum}`;
        markVerseAsRead(quranData.selectedSurah, verseKey);
      }
    }
  });

  // Wrap audio controls with error handling
  const handleTogglePlay = useCallback(() => {
    try {
      quranAudio.togglePlay();
      setAudioError(null);
    } catch (error) {
      console.error('Error toggling audio playback:', error);
      setAudioError('Unable to play audio. Please try again.');
      
      // If we have an audio context, try to resume it
      if (quranAudio.audioContext && quranAudio.audioContext.state === 'suspended') {
        quranAudio.audioContext.resume().catch(console.error);
      }
    }
  }, [quranAudio]);

  // Add enhanced scroll hook
  const { scrollToVerse, clearScrollAttempts } = useEnhancedScroll();
  const [isScrolling, setIsScrolling] = useState(false);

  // Inside Quran component, add new refs to control scroll behavior
  const mountedVerses = useRef(new Set());
  const scrollTimeoutRef = useRef(null);
  const initialAutoScrollCompleteRef = useRef(false);
  const ignoreReadingProgressUpdatesRef = useRef(false);

  // Update handleVerseScroll to use useEnhancedScroll and correct container
  const handleVerseScroll = useCallback((verseIndex, options = {}) => {
    const { immediate = false, onSuccess, onError } = options;
    // Dynamically select the correct scrollable container
    const containerRef = isMobileOrTabletPortrait ? contentContainerRef : scrollContainerRef;
    // Use useEnhancedScroll's scrollToVerse
    scrollToVerse(verseIndex, {
      scrollContainerRef: containerRef,
      verseRefs,
      onSuccess: () => {
        setIsScrolling(false);
        onSuccess?.();
      },
      onError: (err) => {
        setIsScrolling(false);
        onError?.(err);
      },
      immediate
    });
    setIsScrolling(true);
  }, [isMobileOrTabletPortrait, scrollToVerse]);

  // Add new function to track verse mounting
  const handleVerseMounted = useCallback((index, element) => {
    verseRefs.current[index] = element;
    mountedVerses.current.add(index);
  }, []);

  // Replace waitForRef and waitForVerseElements with a single robust function
  const waitForVerse = useCallback((verseIndex, retryCount = 0) => {
    return new Promise((resolve, reject) => {
      const maxRetries = SCROLL_RETRY_COUNT;
      
      const checkVerse = () => {
        // Check if component is still mounted
        if (!contentContainerRef.current) {
          reject(new Error('Component unmounted'));
          return;
        }

        // Check if we have the verse ref
        if (mountedVerses.current.has(verseIndex) && verseRefs.current[verseIndex]) {
          resolve(verseRefs.current[verseIndex]);
          return;
        }

        // If we've exceeded retries, reject
        if (retryCount >= maxRetries) {
          reject(new Error(`Failed to find verse ${verseIndex} after ${maxRetries} attempts`));
          return;
        }

        // Try again after delay
        setTimeout(() => {
          checkVerse(retryCount + 1);
        }, SCROLL_RETRY_DELAY);
      };

      checkVerse();
    });
  }, []);

  // Update verse click handler
  const handleVerseClick = useCallback((index) => {
    quranData.setCurrentVerse(index);
    handleVerseScroll(index, {
      immediate: false,
      onSuccess: () => {
        quranAudio.playVerse(index);
      }
    });
  }, [quranData.setCurrentVerse, handleVerseScroll, quranAudio.playVerse]);

  // Add cleanup effect
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      mountedVerses.current.clear();
      verseRefs.current = {};
    };
  }, []);

  // Update the scroll effect to use a one-time flag
  useEffect(() => {
    // Skip if we're already scrolling or if we should ignore reading progress updates
    if (quranData.loading || 
        isScrolling || 
        !quranData.surahContent?.length || 
        !quranData.selectedSurah ||
        ignoreReadingProgressUpdatesRef.current) {
      return;
    }
    
    const surahHistory = settings.quran?.readingProgress?.history?.[quranData.selectedSurah] || {};
    const verseNumbers = Object.keys(surahHistory)
      .map(num => parseInt(num))
      .filter(num => !isNaN(num));
    
    // Only scroll to the last read verse on initial load, not on subsequent read progress updates
    if (verseNumbers.length > 0 && !initialAutoScrollCompleteRef.current) {
      const lastReadVerseNum = Math.max(...verseNumbers);
      const verseIndex = quranData.surahContent.findIndex(
        verse => verse.verse_key === `${quranData.selectedSurah}:${lastReadVerseNum}`
      );
      
      if (verseIndex !== -1) {
        console.log('🎯 Attempting initial auto-scroll to verse:', verseIndex);
        
        // Set the flag to prevent further auto-scrolls
        initialAutoScrollCompleteRef.current = true;
        
        // Temporarily ignore reading progress updates during auto-scroll
        ignoreReadingProgressUpdatesRef.current = true;
        
        const attemptScroll = async (attempt = 1) => {
          try {
            console.log(`📜 Initial scroll attempt ${attempt} for verse ${verseIndex}`);
            
            // Wait for the verse to be available
            const verseElement = await waitForVerse(verseIndex);
            console.log('✅ Verse element found, executing initial scroll');
            
            // Scroll to the verse
            handleVerseScroll(verseIndex, {
              immediate: true,
              onSuccess: () => {
                console.log('✨ Initial scroll completed successfully');
                
                // Re-enable reading progress updates after a delay
                setTimeout(() => {
                  ignoreReadingProgressUpdatesRef.current = false;
                }, 5000); // 5 second delay to let the user start reading
              },
              onError: (error) => {
                console.error('❌ Initial scroll failed:', error);
                ignoreReadingProgressUpdatesRef.current = false;
                
                if (attempt < SCROLL_RETRY_COUNT) {
                  console.log(`🔄 Retrying initial scroll (attempt ${attempt + 1})`);
                  setTimeout(() => attemptScroll(attempt + 1), SCROLL_RETRY_DELAY);
                }
              }
            });
          } catch (error) {
            console.error(`❌ Failed to find verse for initial scroll (attempt ${attempt}):`, error);
            ignoreReadingProgressUpdatesRef.current = false;
            
            if (attempt < SCROLL_RETRY_COUNT) {
              console.log(`🔄 Retrying verse lookup for initial scroll (attempt ${attempt + 1})`);
              setTimeout(() => attemptScroll(attempt + 1), SCROLL_RETRY_DELAY);
            }
          }
        };
        
        // Start the scroll attempt
        attemptScroll();
      }
    }
  }, [
    quranData.loading,
    quranData.surahContent,
    quranData.selectedSurah,
    settings.quran?.readingProgress?.history,
    isScrolling,
    handleVerseScroll,
    waitForVerse
  ]);

  // Reset the initial scroll flag when surah changes
  useEffect(() => {
    console.log('🔄 Surah changed to:', quranData.selectedSurah, '- Resetting auto-scroll flags');
    
    // Reset all auto-scroll flags when surah changes
    initialAutoScrollCompleteRef.current = false;
    ignoreReadingProgressUpdatesRef.current = false;
    
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      mountedVerses.current.clear();
      verseRefs.current = {};
    };
  }, [quranData.selectedSurah]);

  // Memoize the verse intersection handler
  const handleVerseIntersection = useCallback((entries) => {
    if (!isReadingMode || !quranData.selectedSurah || !isSettingsInitialized) {
      return;
    }

    const versesToUpdate = new Set();
    const processedVerseNumbers = new Set(); // Track verse numbers being processed

    entries.forEach(entry => {
      const verseId = entry.target.getAttribute('data-verse-id');
      if (!verseId) return;

      // Use an even lower threshold for considering a verse as read (0.2 instead of 0.3)
      // This increases the chance of capturing all verses during rapid scrolling
      if (entry.isIntersecting && entry.intersectionRatio >= 0.2) {
        const [surahId, verseNum] = verseId.split(':');
        // Get the verse number as a number
        const verseNumInt = parseInt(verseNum);
        processedVerseNumbers.add(verseNumInt);
        
        // Mark the current verse as read
        if (!settings.quran?.readingProgress?.history?.[surahId]?.[verseNum] && !observedVerses.current.has(verseId)) {
          versesToUpdate.add(verseId);
          observedVerses.current.add(verseId);
          
          // Also mark up to 5 previous verses to ensure continuity (increased from 3)
          // This helps catch verses that might have been missed during fast scrolling
          for (let i = 1; i <= 5; i++) {
            if (verseNumInt - i > 0) {
              const prevVerseId = `${surahId}:${verseNumInt - i}`;
              const prevVerseNum = `${verseNumInt - i}`;
              
              // Only mark if not already marked
              if (!settings.quran?.readingProgress?.history?.[surahId]?.[prevVerseNum] && 
                  !observedVerses.current.has(prevVerseId)) {
                versesToUpdate.add(prevVerseId);
                observedVerses.current.add(prevVerseId);
                console.log(`📚 Filling in previous verse: ${prevVerseId}`);
              } else {
                // Stop filling in previous verses once we hit one that's already marked
                break;
              }
            }
          }
        }
      }
    });

    // After processing all entries, look for gaps in the sequence of verses being processed
    if (processedVerseNumbers.size > 1) {
      const surahId = quranData.selectedSurah;
      const sortedVerseNumbers = Array.from(processedVerseNumbers).sort((a, b) => a - b);
      
      for (let i = 0; i < sortedVerseNumbers.length - 1; i++) {
        const currentVerse = sortedVerseNumbers[i];
        const nextVerse = sortedVerseNumbers[i + 1];
        
        // If there's a gap and it's reasonable (less than 5 verses), fill it
        if (nextVerse - currentVerse > 1 && nextVerse - currentVerse < 5) {
          for (let j = currentVerse + 1; j < nextVerse; j++) {
            const gapVerseId = `${surahId}:${j}`;
            
            // Only mark if not already marked
            if (!settings.quran?.readingProgress?.history?.[surahId]?.[j] && 
                !observedVerses.current.has(gapVerseId)) {
              versesToUpdate.add(gapVerseId);
              observedVerses.current.add(gapVerseId);
              console.log(`🔍 Filling sequence gap for verse: ${gapVerseId}`);
            }
          }
        }
      }
    }

    // Debug log for tracking
    if (versesToUpdate.size > 0) {
      console.log(`🔖 Marking ${versesToUpdate.size} verses as read:`, Array.from(versesToUpdate));
    }

    // Only update if we have new verses to mark as read
    if (versesToUpdate.size > 0) {
      const [surahId] = Array.from(versesToUpdate)[0].split(':');
      const updatedContent = quranData.surahContent ? [...quranData.surahContent] : [];

      // Sort verses numerically for ordered processing
      const orderedVerses = Array.from(versesToUpdate)
        .map(id => {
          const [sid, vnum] = id.split(':');
          return { id, surahId: sid, verseNum: parseInt(vnum) };
        })
        .sort((a, b) => a.verseNum - b.verseNum);

      // Mark verses in ascending order to ensure proper sequence
      orderedVerses.forEach(({ id, surahId }) => {
        try {
          markVerseAsRead(surahId, id);
          const verseIndex = updatedContent.findIndex(v => v.verse_key === id);
          if (verseIndex !== -1) {
            updatedContent[verseIndex] = { ...updatedContent[verseIndex] };
          }
        } catch (error) {
          console.error(`Error marking verse ${id} as read:`, error);
        }
      });

      // Only update content if we have changes
      if (updatedContent.length > 0) {
        quranData.setSurahContent(updatedContent);
      }
    }
  }, [
    isReadingMode,
    quranData.selectedSurah,
    isSettingsInitialized,
    settings.quran?.readingProgress?.history,
    markVerseAsRead,
    quranData.surahContent,
    quranData.setSurahContent
  ]);

  // Setup unified intersection observer with cleanup
  useEffect(() => {
    if (!quranData.surahContent || !isReadingMode || !quranData.selectedSurah || !contentContainerRef.current) {
      return;
    }

    // Reset observer setup state when surah changes
    if (quranData.selectedSurah !== previousSurahRef.current) {
      isObserverSetup.current = false;
      observedVerses.current = new Set();
      previousSurahRef.current = quranData.selectedSurah;
    }

    // Only set up observer if not already setup for this surah
    if (isObserverSetup.current) {
      return;
    }

    isObserverSetup.current = true;
    let isObserverActive = true;
    
    // Use multiple thresholds to better catch verses during fast scrolling
    const observer = new IntersectionObserver(
      (entries) => {
        if (!isObserverActive) return;
        handleVerseIntersection(entries);
      },
      {
        threshold: [0.1, 0.2, 0.3, 0.5, 0.7], // Added more thresholds for finer granularity
        rootMargin: '150px 0px 150px 0px' // Increased vertical margins further
      }
    );

    // Debounced version of observe verses with cleanup check
    const debouncedObserveVerses = debounce(() => {
      if (!isObserverActive || !contentContainerRef.current) return;
      
      const verseElements = contentContainerRef.current.querySelectorAll('[data-verse-id]');
      
      // Count of newly observed elements for debug
      let newlyObserved = 0;
      
      verseElements.forEach(element => {
        if (!element.dataset.isObserved) {
          observer.observe(element);
          element.dataset.isObserved = 'true';
          newlyObserved++;
        }
      });
      
      if (newlyObserved > 0) {
        console.log(`👁️ Observing ${newlyObserved} new verse elements`);
      }
    }, 25); // Ultra-reduced debounce time (25ms instead of 50ms) for faster response

    // Initial observation
    debouncedObserveVerses();

    // Setup mutation observer with debouncing and cleanup check
    const mutationObserver = new MutationObserver((mutations) => {
      if (!isObserverActive) return;
      
      const hasNewVerses = mutations.some(mutation => 
        Array.from(mutation.addedNodes).some(node => 
          node.nodeType === 1 && (
            node.hasAttribute('data-verse-id') || 
            node.querySelector('[data-verse-id]')
          )
        )
      );

      if (hasNewVerses) {
        debouncedObserveVerses();
      }
    });

    if (contentContainerRef.current) {
      mutationObserver.observe(contentContainerRef.current, {
        childList: true,
        subtree: true
      });
    }

    // Add a scroll event listener for more reliable verse tracking
    const handleScroll = debounce(() => {
      if (!isObserverActive || !contentContainerRef.current) return;
      
      // Force re-calculation of intersections on scroll
      const verseElements = contentContainerRef.current.querySelectorAll('[data-verse-id]');
      
      // Only process elements that are likely to be in or near viewport to improve performance
      const viewportTop = window.scrollY;
      const viewportBottom = viewportTop + window.innerHeight;
      const viewportBuffer = 300; // Check elements within 300px of viewport
      
      verseElements.forEach(element => {
        if (element.dataset.isObserved === 'true') {
          const rect = element.getBoundingClientRect();
          const elementTop = rect.top + viewportTop;
          const elementBottom = rect.bottom + viewportTop;
          
          // Only recalculate for elements near the viewport
          if (elementBottom + viewportBuffer >= viewportTop && elementTop - viewportBuffer <= viewportBottom) {
            // This forces the browser to recalculate the element's position
            observer.unobserve(element);
            observer.observe(element);
          }
        }
      });
      
      // Also check for any gaps during scroll
      // This helps catch verses that may have been missed
      const allVerseIds = Array.from(verseElements)
        .map(el => el.getAttribute('data-verse-id'))
        .filter(Boolean);
      
      if (allVerseIds.length > 0) {
        const [firstSurahId] = allVerseIds[0].split(':');
        
        // Find gaps in the observed verses
        allVerseIds.forEach((verseId, index) => {
          if (index === 0) return; // Skip first verse
          
          const [_, currentVerseNum] = verseId.split(':');
          const [__, prevVerseNum] = allVerseIds[index - 1].split(':');
          
          const currNum = parseInt(currentVerseNum);
          const prevNum = parseInt(prevVerseNum);
          
          // If there's a gap and gap is small (<=5), fill it
          if (currNum - prevNum > 1 && currNum - prevNum <= 5) {
            for (let i = prevNum + 1; i < currNum; i++) {
              const gapVerseId = `${firstSurahId}:${i}`;
              
              // Only mark if not already marked
              if (!settings.quran?.readingProgress?.history?.[firstSurahId]?.[i] && 
                  !observedVerses.current.has(gapVerseId)) {
                try {
                  markVerseAsRead(firstSurahId, gapVerseId);
                  observedVerses.current.add(gapVerseId);
                  console.log(`🧩 Filling scroll gap for verse: ${gapVerseId}`);
                } catch (error) {
                  console.error(`Error filling gap for verse ${gapVerseId}:`, error);
                }
              }
            }
          }
        });
      }
    }, 50); // Faster response time during scrolling

    scrollContainerRef.current?.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      isObserverActive = false;
      observer.disconnect();
      mutationObserver.disconnect();
      isObserverSetup.current = false;
      scrollContainerRef.current?.removeEventListener('scroll', handleScroll);
      
      if (contentContainerRef.current) {
        contentContainerRef.current.querySelectorAll('[data-verse-id]').forEach(el => {
          delete el.dataset.isObserved;
        });
      }
    };
  }, [handleVerseIntersection, isReadingMode, quranData.selectedSurah, quranData.surahContent]);

  // Handle surah loading from navigation with proper cleanup
  useEffect(() => {
    // Remove console.log statements
    // console.log('📖 Checking navigation state:', {
    //   isSettingsInitialized,
    //   targetSurah: location.state?.surahId,
    //   targetVerse: location.state?.verseId
    // });

    if (!isSettingsInitialized) return;

    let isEffectActive = true;
    const targetSurah = location.state?.surahId;
    const targetVerse = location.state?.verseId;

    if (targetSurah && quranData.loadSurahContent && targetSurah !== quranData.selectedSurah) {
      // Remove console.log
      // console.log('🎯 Loading target surah and verse:', { targetSurah, targetVerse });
      setShowNavigation(false);
      quranData.loadSurahContent(targetSurah, targetVerse);
    }

    return () => {
      isEffectActive = false;
    };
  }, [isSettingsInitialized, location.state?.surahId, location.state?.verseId, quranData.loadSurahContent, quranData.selectedSurah]);

  // Click outside handler for footnotes
  useEffect(() => {
    // Remove console.log
    // console.log('Quran - Click outside effect');
    const handleClickOutside = (event) => {
      if (footnoteRef.current && !footnoteRef.current.contains(event.target)) {
        setActiveFootnote(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Add useEffect to handle first load
  useEffect(() => {
    // Remove console.log
    // console.log('Quran - First load effect');
    setIsFirstLoad(false);
  }, []);

  // Add useEffect to reset isInternalNavigation when unmounting
  useEffect(() => {
    // Remove console.log
    // console.log('Quran - Internal navigation reset effect');
    return () => {
      setIsInternalNavigation(undefined);
    };
  }, []);

  // Load last position effect - simplified
  useEffect(() => {
    // Skip if we have a surahId in the location state
    if (location.state?.surahId) return;

    if (!isSettingsInitialized || !quranSettings.lastReadingSession) return;

    const { surahId, verseId } = quranSettings.lastReadingSession;
    if (surahId && !quranData.selectedSurah) {
      setShowNavigation(false);
      quranData.loadSurahContent(surahId, verseId);
    }
  }, [
    isSettingsInitialized,
    quranSettings.lastReadingSession,
    quranData.selectedSurah,
    quranData.loadSurahContent,
    location.state?.surahId
  ]);

  // Memoize the verse intersection handler for loading previous verses
  const handleTopIntersection = useCallback((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting && !quranData.loading && !quranData.isLoadingMore && quranData.currentPage > 1) {
        quranData.loadPreviousVerses();
      }
    });
  }, [quranData.loading, quranData.isLoadingMore, quranData.currentPage, quranData.loadPreviousVerses]);

  // Setup top intersection observer
  useEffect(() => {
    if (!contentContainerRef.current) return;

    const observer = new IntersectionObserver(
      handleTopIntersection,
      {
        root: null,
        threshold: 0.1,
        rootMargin: '200px'
      }
    );

    if (topObserverTarget.current) {
      observer.observe(topObserverTarget.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [handleTopIntersection]);

  // Add scroll handler to show/hide scroll to top button
  useEffect(() => {
    // Use contentContainerRef, which is the actual scrolling element
    if (!contentContainerRef.current) return;

    const handleScroll = () => {
      // Use contentContainerRef here as well
      const scrollTop = contentContainerRef.current.scrollTop;
      setShowScrollToTop(scrollTop > 500);
    };

    // Attach listener to contentContainerRef
    const scrollContainer = contentContainerRef.current;
    scrollContainer.addEventListener('scroll', handleScroll);
    // Cleanup function should also use the captured variable
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
    // Add contentContainerRef.current to dependencies if needed, 
    // though typically refs don't need to be dependencies unless their assignment changes
  }, []); // Keep dependencies minimal, ref.current changes don't trigger re-renders

  // Add scroll to top function
  const scrollToTop = () => {
    // Use contentContainerRef to scroll the correct element
    if (contentContainerRef.current) {
      contentContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  // Other utility functions
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  // Handle resize and orientation changes
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const isPortrait = window.matchMedia("(orientation: portrait)").matches;
      setIsMobileOrTabletPortrait(width <= 768 || (width <= 1024 && isPortrait));
    };

    window.addEventListener('resize', handleResize);
    
    // Some devices report orientation change as resize, but add this just in case
    window.addEventListener('orientationchange', () => {
      // Small delay to ensure dimensions have updated
      setTimeout(handleResize, 100);
    });

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  const toggleRepeat = (index) => {
    if (repeatingVerse === index) {
      setRepeatingVerse(null);
    } else {
      setRepeatingVerse(index);
    }
  };

  // Format footnotes
  const formatFootnotes = (text) => {
    if (!text) return '';
    
    return text.split(/(<sup foot_note=\d+>\d+<\/sup>)/).map((part, index) => {
      const footnoteMatch = part.match(/<sup foot_note=(\d+)>(\d+)<\/sup>/);
      if (footnoteMatch) {
        const footnoteId = footnoteMatch[1];
        const footnoteNumber = footnoteMatch[2];
        
        return (
          <span key={index} className="relative inline-block">
            <sup 
              ref={activeFootnote === footnoteId ? footnoteRef : null}
              className="text-xs text-green-600 dark:text-green-400 ml-0.5 cursor-pointer hover:text-green-800 dark:hover:text-green-300"
              onMouseEnter={() => setActiveFootnote(footnoteId)}
              onMouseLeave={() => setActiveFootnote(null)}
            >
              {footnoteNumber}
            </sup>
            {activeFootnote === footnoteId && quranData.footnotes[footnoteId] && (
              <div 
                className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 p-2 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 rounded shadow-lg border border-gray-200 dark:border-gray-600 z-50"
                onMouseEnter={() => setActiveFootnote(footnoteId)}
                onMouseLeave={() => setActiveFootnote(null)}
              >
                <div className="relative">
                  <div className="p-2">{quranData.footnotes[footnoteId]}</div>
                  <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 rotate-45 w-3 h-3 bg-white dark:bg-gray-700 border-r border-b border-gray-200 dark:border-gray-600"></div>
                </div>
              </div>
            )}
          </span>
        );
      }
      return part;
    });
  };

  // Prepare options for react-select
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: isDarkMode ? '#111111' : 'white',
      borderColor: state.isFocused ? '#10B981' : isDarkMode ? '#1A1A1A' : '#D1D5DB',
      boxShadow: state.isFocused ? '0 0 0 1px #10B981' : null,
      color: isDarkMode ? 'white' : 'black',
      '&:hover': {
        borderColor: '#10B981'
      }
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected 
        ? '#10B981' 
        : state.isFocused 
          ? isDarkMode ? '#1A1A1A' : '#E5E7EB'
          : isDarkMode ? '#111111' : null,
      color: state.isSelected 
        ? 'white' 
        : isDarkMode 
          ? 'white' 
          : 'black',
      padding: '8px 12px',
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: isDarkMode ? '#0A0A0A' : 'white',
      borderColor: isDarkMode ? '#1A1A1A' : '#D1D5DB',
      borderWidth: '1px',
      borderRadius: '0.75rem',
      padding: '0.5rem',
      boxShadow: isDarkMode ? '0 10px 15px -3px rgba(0, 0, 0, 0.3)' : '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    }),
    menuList: (provided) => ({
      ...provided,
      backgroundColor: isDarkMode ? '#0A0A0A' : 'white',
      borderRadius: '0.5rem',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: isDarkMode ? 'white' : 'black',
    }),
  };

  const surahOptions = quranData.surahs.map(surah => ({
    value: surah.id.toString(),
    label: `${surah.id}. ${surah.name_simple} (${quranData.getMemorizedCount(surah.id.toString())})`
  }));

  const reciterOptions = quranData.reciters.map(reciter => ({
    value: reciter.id.toString(),
    label: reciter.reciter_name
  }));

  const languageOptions = quranData.languages.map(language => ({
    value: language.iso_code,
    label: language.native_name || language.name || language.iso_code
  }));

  const translationOptions = quranData.translations
    .filter(translation => {
      const selectedLangName = quranData.languages.find(lang => lang.iso_code === quranData.selectedLanguage)?.name;
      return translation.language_name.toLowerCase() === selectedLangName?.toLowerCase() &&
        (quranData.selectedLanguage !== 'en' || translation.name.toLowerCase() !== 'transliteration');
    })
    .map(translation => ({
      value: translation.id.toString(),
      label: translation.name || `Translation ${translation.id}`
    }));

  // Settings handlers - simplified
  const handleQuranModeChange = useCallback((mode) => {
    if (!isSettingsInitialized) return;
    updateSettings({
      ...settings,
      quran: { ...quranSettings, mode }
    });
  }, [isSettingsInitialized, settings, quranSettings, updateSettings]);

  const handleDisplayModeChange = useCallback((displayMode) => {
    if (!isSettingsInitialized) return;
    updateSettings({
      ...settings,
      quran: { ...quranSettings, displayMode }
    });
  }, [isSettingsInitialized, settings, quranSettings, updateSettings]);

  const handleTransliterationChange = useCallback((showTransliteration) => {
    if (!isSettingsInitialized) return;
    updateSettings({
      ...settings,
      quran: { ...quranSettings, showTransliteration }
    });
  }, [isSettingsInitialized, settings, quranSettings, updateSettings]);

  const handleLanguageChange = useCallback((option) => {
    if (!isSettingsInitialized || !option?.value) return;
    updateSettings({
      ...settings,
      quran: { ...quranSettings, selectedLanguage: option.value }
    });
  }, [isSettingsInitialized, settings, quranSettings, updateSettings]);

  const handleFontSizeChange = useCallback((fontSize) => {
    if (!isSettingsInitialized) return;
    updateSettings({
      ...settings,
      quran: { ...quranSettings, fontSize }
    });
  }, [isSettingsInitialized, settings, quranSettings, updateSettings]);

  const handleReciterChange = useCallback((option) => {
    if (!isSettingsInitialized || !option?.value) return;
    updateSettings({
      ...settings,
      quran: { ...quranSettings, selectedReciter: option.value }
    });
  }, [isSettingsInitialized, settings, quranSettings, updateSettings]);

  const handleTranslationChange = useCallback((option) => {
    if (!isSettingsInitialized || !option?.value) return;
    updateSettings({
      ...settings,
      quran: { ...quranSettings, selectedTranslation: option.value }
    });
  }, [isSettingsInitialized, settings, quranSettings, updateSettings]);

  // Navigation handlers
  const handleSurahSelect = (surahIdOrOption) => {
    // Extract surahId from either a string or an object with value property
    let surahId;
    if (typeof surahIdOrOption === 'object' && surahIdOrOption !== null) {
      // It's an option object from react-select { value: "1", label: "Al-Fatiha" }
      surahId = surahIdOrOption.value;
      console.log('Surah selected from dropdown:', surahIdOrOption.label, 'with ID:', surahId);
    } else {
      // It's already a string ID
      surahId = surahIdOrOption;
      console.log('Surah selected with ID:', surahId);
    }

    // Proceed only if we have a valid string ID
    if (typeof surahId === 'string' || typeof surahId === 'number') {
      setShowNavigation(false); // Close navigation page if open
      // Reset initial scroll flag when changing surahs
      initialAutoScrollCompleteRef.current = false;
      quranData.loadSurahContent(surahId);
    } else {
      console.error('Invalid surah ID:', surahIdOrOption);
    }
  };

  const handleHeaderClick = useCallback(() => {
    setShowNavigation(true);
    setIsInternalNavigation(true);
    setNavigationHistory(prev => ({
      previousPage: prev.currentPage,
      currentPage: 'navigation'
    }));
  }, []);

  const handleSettingsClick = useCallback(() => {
    setShowSettings(true);
    setIsSettingsOpen(true);
  }, []);

  const handleBack = useCallback(() => {
    setShowSettings(false);
    setIsSettingsOpen(false);
  }, []);

  const handleSettingsClose = useCallback(() => {
    setShowSettings(false);
    setIsSettingsOpen(false);
    setShowNavigation(settings.openedFrom === 'navigation');
  }, [settings.openedFrom]);

  // Reset handlers
  const handleResetMemorization = useCallback(() => {
    if (!isSettingsInitialized || !settings.quran) return;
    
    updateSettings({
      ...settings,
      quran: {
        ...settings.quran,
        memorization: {}
      }
    });
  }, [settings, updateSettings, isSettingsInitialized]);

  const handleResetReadingProgress = useCallback(() => {
    if (!isSettingsInitialized || !settings.quran) return;

    updateSettings({
      ...settings,
      quran: {
        ...settings.quran,
        readingProgress: {
          currentSession: {
            surah: null,
            verse: null,
            timestamp: null
          },
          history: {}
        }
      }
    });
  }, [settings, updateSettings, isSettingsInitialized]);

  // Open note modal/sheet
  const handleNoteClick = (verseKey) => {
    const isMobile = isMobileOrTabletPortrait; // Use the state variable
    console.log('Note button clicked for verse:', verseKey, 'isMobile:', isMobile);
    setCurrentNoteVerse(verseKey);
    setNoteText(quranData.notes[verseKey] || ''); // Load existing note text

    if (isMobile) {
      setShowNoteSheet(true);
      setShowNoteModal(false); // Ensure modal is closed
    } else {
      setShowNoteModal(true);
      setShowNoteSheet(false); // Ensure sheet is closed
    }
  };

  // Save note
  const handleSaveNote = async () => {
    if (currentNoteVerse) {
      try {
        // Call the function from quranData to save the note
        await quranData.handleSaveNote(currentNoteVerse, noteText);
        setShowNoteSheet(false); // Close sheet
        setShowNoteModal(false); // Close modal
      } catch (error) {
        console.error("Error saving note:", error);
        // Optionally show an error message to the user
      }
    }
  };

  // Render helpers
  const renderVerseContent = useCallback(() => {
    // Don't render anything until settings are initialized
    if (!isSettingsInitialized) {
      return <LoadingState isDarkMode={isDarkMode} />;
    }

    // Core state checks
    const isLoading = quranData.loading;
    const hasLastReadingSession = quranSettings.lastReadingSession?.surahId;
    const hasSelectedSurah = quranData.selectedSurah;
    const hasValidContent = quranData.surahContent?.length > 0;
    const hasLocationSurah = location.state?.surahId;

    // Loading states - Show skeletons during loading and transitions
    const shouldShowLoading = isLoading || (isFirstLoad && hasLastReadingSession && !hasValidContent);

    // Show loading state first - this takes precedence
    if (shouldShowLoading) {
      return (
        <motion.div
          key="loading"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="space-y-4"
        >
          {Array.from({ length: 15 }).map((_, index) => (
            <SkeletonVerse 
              key={index} 
              isDarkMode={isDarkMode}
              className="mx-4 my-2"
            />
          ))}
        </motion.div>
      );
    }

    // Then check if we should show navigation
    if (!hasSelectedSurah && 
        !hasLocationSurah && 
        !hasLastReadingSession) {
      return (
        <QuranNavigationPage
          surahs={quranData.surahs}
          onSurahSelect={handleSurahSelect}
          selectedSurah={quranData.selectedSurah}
          getMemorizedCount={quranData.getMemorizedCount}
          getReadingProgress={quranData.getReadingProgress}
          onSettingsClick={handleSettingsClick}
          onClose={() => setShowNavigation(false)}
          animate={isInternalNavigation}
          isSettingsOpen={isSettingsOpen}
          quranMode={settings.quran?.mode}
          lastReadingSession={settings.quran?.lastReadingSession}
          isDarkMode={isDarkMode}
        />
      );
    }

    // Finally, render content if we have it
    if (hasValidContent) {
      return (
        <motion.div
          key="content"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            ref={scrollContainerRef} 
            className="space-y-6 md:px-8 pt-4"
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.2 }}
              className={`${quranData.displayMode === 'arabic-only' ? 'px-4' : ''}`}
            >
              {quranData.displayMode === 'arabic-only' ? (
                <ArabicOnlyDisplay
                  verses={quranData.surahContent}
                  currentVerse={quranData.currentVerse}
                  handleVerseClick={(index) => {
                    quranData.setCurrentVerse(index);
                    handleVerseScroll(index);
                  }}
                  isDarkMode={isDarkMode}
                  fontSize={settings.quran?.fontSize}
                  isRead={settings.quran?.readingProgress?.history?.[quranData.selectedSurah] || {}}
                  onVerseRead={(verseNum) => markVerseAsRead(quranData.selectedSurah, `${quranData.selectedSurah}:${verseNum}`)}
                />
              ) : (
                quranData.surahContent.map((verse, index) => {
                  const verseKey = verse.verse_key;
                  const [surahId, verseNum] = verseKey.split(':');
                  const isRead = settings.quran?.readingProgress?.history?.[surahId]?.[verseNum] || false;
                  
                  return (
                    <VerseDisplay
                      key={verseKey}
                      ref={(el) => {
                        if (el) handleVerseMounted(index, el);
                      }}
                      verse={verse}
                      index={index}
                      isDarkMode={isDarkMode}
                      displayMode={quranData.displayMode}
                      currentVerse={quranData.currentVerse}
                      handleVerseClick={handleVerseClick}
                      selectedTranslation={quranData.selectedTranslation}
                      memorizedVerses={quranData.memorizedVerses}
                      handleMemorizedToggle={(verseKey) => quranData.handleMemorizedToggle(verseKey, !quranData.memorizedVerses[verseKey])}
                      handleNoteClick={handleNoteClick}
                      repeatingVerse={repeatingVerse}
                      toggleRepeat={toggleRepeat}
                      notes={quranData.notes}
                      isRead={isRead}
                      fontSize={settings.quran?.fontSize}
                      showTransliteration={quranData.showTransliteration}
                      formatFootnotes={formatFootnotes}
                      onVerseRead={(vNum) => markVerseAsRead(surahId, `${surahId}:${vNum}`)}
                    />
                  );
                })
              )}
            </motion.div>

            {/* Next Surah Button */}
            {quranData.selectedSurah && quranData.surahs && parseInt(quranData.selectedSurah) < 114 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.3 }}
                className="flex justify-center py-8"
              >
                <motion.button
                  onClick={() => handleSurahSelect((parseInt(quranData.selectedSurah) + 1).toString())}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`flex items-center gap-2 px-6 py-3 rounded-lg shadow-lg transition-colors ${
                    isDarkMode
                      ? 'bg-[#111111] text-emerald-400 hover:bg-[#1A1A1A] hover:text-emerald-300 ring-1 ring-white/5'
                      : 'bg-white text-emerald-600 hover:bg-gray-50 hover:text-emerald-500 ring-1 ring-black/5'
                  }`}
                >
                  <Book className="w-5 h-5" />
                  <span className="font-medium">Go to Next Surah</span>
                </motion.button>
              </motion.div>
            )}
            
            {/* Scroll to Top Button */}
            <AnimatePresence>
              {showScrollToTop && !showNavigation && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.5 }}
                  onClick={scrollToTop}
                  className={`fixed right-4 p-3 rounded-full shadow-lg backdrop-blur-sm transition-colors z-50 ${
                    isDarkMode
                      ? 'bg-[#111111]/80 text-emerald-400 hover:bg-[#1A1A1A]/80 hover:text-emerald-300 ring-1 ring-white/5'
                      : 'bg-white/80 text-emerald-600 hover:bg-white hover:text-emerald-500 ring-1 ring-black/5'
                  } ${window.innerWidth <= 768 ? 'bottom-24' : 'bottom-[88px]'}`}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <ChevronUp className="w-5 h-5" />
                </motion.button>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      );
    }

    return null;
  }, [
    quranData.loading,
    quranData.selectedSurah,
    quranData.surahContent,
    isDarkMode,
    quranData.displayMode,
    quranData.currentVerse,
    quranData.selectedTranslation,
    quranData.memorizedVerses,
    repeatingVerse,
    quranData.notes,
    settings.quran?.readingProgress?.history,
    settings.quran?.fontSize,
    quranData.showTransliteration,
    handleVerseScroll,
    quranData.handleMemorizedToggle,
    handleNoteClick,
    toggleRepeat,
    formatFootnotes,
    isInternalNavigation,
    isSettingsOpen,
    settings.quran?.mode,
    settings.quran?.lastReadingSession,
    quranData.surahs,
    quranData.getMemorizedCount,
    quranData.getReadingProgress,
    handleSurahSelect,
    handleSettingsClick,
    location,
    showScrollToTop,
    showNavigation,
    scrollToTop,
    isFirstLoad,
    quranSettings.lastReadingSession
  ]);

  const renderMobileView = () => (
    <div className={`min-h-screen h-full ${isDarkMode ? 'bg-[#0A0A0A]' : 'bg-gray-50'}`}>
      {!showNavigation && (
        <QuranHeader
          surah={quranData.surahs.find(s => s.id.toString() === quranData.selectedSurah)}
          onHeaderClick={handleHeaderClick}
          onSettingsClick={handleSettingsClick}
          isDarkMode={isDarkMode}
          isNavigationOpen={showNavigation}
          isNavigationPage={showNavigation}
        />
      )}
      <div className="relative h-full">
        <motion.div
          initial={false}
          animate={{ x: showSettings ? '-15%' : 0 }}
          transition={{ type: 'spring', bounce: 0, duration: 0.4 }}
          className={`relative h-full ${isDarkMode ? 'bg-[#0A0A0A]' : 'bg-gray-50'}`}
        >
          <AnimatePresence>
            {showNavigation && (
              <QuranNavigationPage
                surahs={quranData.surahs}
                onSurahSelect={handleSurahSelect}
                selectedSurah={quranData.selectedSurah}
                getMemorizedCount={quranData.getMemorizedCount}
                getReadingProgress={quranData.getReadingProgress}
                onSettingsClick={handleSettingsClick}
                onClose={() => setShowNavigation(false)}
                animate={isInternalNavigation}
                isSettingsOpen={isSettingsOpen}
                quranMode={settings.quran?.mode}
                lastReadingSession={settings.quran?.lastReadingSession}
                isDarkMode={isDarkMode}
              />
            )}
          </AnimatePresence>

          <div 
            ref={contentContainerRef}
            className={`h-full overflow-y-auto pb-40 ${showNavigation ? 'hidden' : 'block'}`}
          >
            <AnimatePresence mode="wait">
            {renderVerseContent()}
            </AnimatePresence>
          </div>
        </motion.div>

        <AnimatePresence>
          {showSettings && (
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', bounce: 0, duration: 0.4 }}
              className="fixed inset-0 z-50"
            >
              <QuranSettings
                show={true}
                onClose={handleBack}
                onTransitionStart={() => setIsSettingsOpen(true)}
                onTransitionEnd={() => setIsSettingsOpen(false)}
                isDarkMode={isDarkMode}
                quranMode={settings.quran?.mode}
                onQuranModeChange={handleQuranModeChange}
                displayMode={quranData.displayMode}
                onDisplayModeChange={handleDisplayModeChange}
                showTransliteration={quranData.showTransliteration}
                onTransliterationChange={handleTransliterationChange}
                fontSize={settings.quran?.fontSize}
                onFontSizeChange={handleFontSizeChange}
                selectedReciter={quranData.selectedReciter}
                onReciterChange={handleReciterChange}
                selectedLanguage={quranData.selectedLanguage}
                onLanguageChange={handleLanguageChange}
                selectedTranslation={quranData.selectedTranslation}
                onTranslationChange={handleTranslationChange}
                reciterOptions={reciterOptions}
                languageOptions={languageOptions}
                translationOptions={translationOptions}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <MobilePlayer
        isDarkMode={isDarkMode}
        isPlaying={quranAudio.isPlaying}
        togglePlay={handleTogglePlay}
        handleVerseClick={handleVerseClick}
        currentVerse={quranData.currentVerse}
        setCurrentVerse={(verse) => {
          if (typeof quranData.setCurrentVerse === 'function') {
            console.log('Setting current verse to:', verse);
            quranData.setCurrentVerse(verse);
          } else {
            console.error('quranData.setCurrentVerse is not a function');
          }
        }}
        repeatMode={quranData.repeatMode}
        handleRepeatClick={quranData.handleRepeatClick}
        isMuted={quranData.isMuted}
        toggleMute={quranData.toggleMute}
        volume={quranData.volume}
        handleVolumeChange={quranData.handleVolumeChange}
        selectedSurah={quranData.selectedSurah}
        surahs={quranData.surahs}
        surahContent={quranData.surahContent}
        audioProgress={quranAudio.audioProgress}
        audioDuration={quranAudio.audioDuration}
        setActiveFootnote={setActiveFootnote}
        activeFootnote={activeFootnote}
        selectedTranslation={quranData.selectedTranslation}
      />
    </div>
  );

  const renderDesktopView = () => (
    <div className={`flex flex-col h-screen overflow-hidden ${isDarkMode ? 'bg-[#0A0A0A]' : 'bg-white'}`}> 
      {/* Sidebar Toggle Button - Only visible when sidebar is closed on smaller large screens */}
      <button
        className={`lg:hidden fixed top-4 left-4 z-40 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} p-2 rounded-full transition-opacity ${sidebarOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
        onClick={toggleSidebar}
      >
        <HiMenu className={`h-6 w-6 ${isDarkMode ? 'text-white' : 'text-black'}`} />
      </button>
      <div className="flex flex-1 overflow-hidden">
        {/* Pass all necessary props to QuranSidebar */}
        <QuranSidebar
          isDarkMode={isDarkMode}
          isMobile={false} // Explicitly false for desktop view
          sidebarOpen={sidebarOpen}
          // onClose={() => setSidebarOpen(false)} // Use onClose from props if needed, otherwise remove if unused
          customStyles={customStyles} // Pass memoized styles
          
          // Options and Handlers from quranData and settings
          surahOptions={surahOptions}
          handleSurahChange={handleSurahSelect} // Use handleSurahSelect for change
          selectedSurah={quranData.selectedSurah}
          
          reciterOptions={reciterOptions}
          handleReciterChange={handleReciterChange}
          selectedReciter={quranSettings.selectedReciter} // Get from quranSettings
          
          languageOptions={languageOptions}
          handleLanguageChange={handleLanguageChange}
          selectedLanguage={quranSettings.selectedLanguage} // Get from quranSettings
          
          translationOptions={translationOptions}
          handleTranslationChange={handleTranslationChange}
          selectedTranslation={quranSettings.selectedTranslation} // Get from quranSettings
          
          // Display settings
          displayMode={quranSettings.displayMode}
          setDisplayMode={handleDisplayModeChange} // Use the correct handler function name
          showTransliteration={quranSettings.showTransliteration}
          handleTransliterationToggle={handleTransliterationChange} // Use the correct handler function name

          // Memorization progress (ensure these exist in quranData or provide defaults)
          memorizedPercentage={quranData.memorizationData?.memorizedPercentage || 0}
          memorizedCount={quranData.memorizationData?.memorizedCount || 0}
          totalVerses={quranData.memorizationData?.totalVerses || 0}

          // Pass compatibility props if needed by the updated sidebar
          // isOpen={sidebarOpen} // Already handled by sidebarOpen
          onClose={() => setSidebarOpen(false)}
          // surahs={quranData.surahs} // Already passed via surahOptions
          // onSurahSelect={handleSurahSelect} // Already passed via handleSurahChange
        />

        {/* Main Content Area */}
        <main 
          ref={scrollContainerRef} // Keep the ref on the main scrollable element
          className={`flex-1 overflow-y-auto transition-all duration-300 ease-in-out ${sidebarOpen ? 'lg:ml-[350px]' : 'lg:ml-0'}`}
        >
          <div ref={contentContainerRef} className="pb-32"> {/* Add padding-bottom for audio player */}
            {renderVerseContent()}
          </div>
        </main>
      </div>

      <AudioPlayer
        isDarkMode={isDarkMode}
        isPlaying={quranAudio.isPlaying}
        togglePlay={handleTogglePlay}
        handleVerseClick={handleVerseClick}
        currentVerse={quranData.currentVerse}
        repeatMode={quranData.repeatMode}
        handleRepeatClick={quranData.handleRepeatClick}
        isMuted={quranData.isMuted}
        toggleMute={quranData.toggleMute}
        volume={quranData.volume}
        handleVolumeChange={quranData.handleVolumeChange}
        selectedSurah={quranData.selectedSurah}
        surahs={quranData.surahs}
        surahContent={quranData.surahContent}
      />
    </div>
  );

  // Render modals and error messages
  const renderModals = () => (
    <>
      {/* Conditionally render NoteModal for Desktop */}
      {!isMobileOrTabletPortrait && (
        <NoteModal
          showNoteModal={showNoteModal}
          setShowNoteModal={setShowNoteModal} // Pass setter for direct closing
          noteText={noteText}
          setNoteText={setNoteText}
          handleSaveNote={handleSaveNote}
          isDarkMode={isDarkMode} // Add isDarkMode for proper styling
          verseKey={currentNoteVerse} // Pass verseKey for context
        />
      )}

      {/* Conditionally render NoteSheet for Mobile */}
      {isMobileOrTabletPortrait && (
        <NoteSheet
          show={showNoteSheet}
          onClose={() => setShowNoteSheet(false)} // Standard close handler
          noteText={noteText}
          setNoteText={setNoteText}
          onSave={handleSaveNote}
          isDarkMode={isDarkMode}
          verseKey={currentNoteVerse}
        />
      )}

      {/* Keep Firestore error display */}
      {quranData.firestoreError && (
        <div className="fixed bottom-20 right-4 m-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 rounded z-50">
          {quranData.firestoreError}
        </div>
      )}

      {/* Keep Mobile Settings Sheet */}
      <AnimatePresence>
        {isSettingsOpen && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className={`fixed inset-0 z-[100] ${isDarkMode ? 'bg-[#0A0A0A]' : 'bg-white'}`}
          >
            <QuranSettingsWrapper 
              onBack={handleBack}
              onClose={() => setIsSettingsOpen(false)} 
              initialPage="quran"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );

  // Add error message display
  const renderErrorMessage = () => {
    if (!audioError) return null;

    return (
      <div className="fixed bottom-24 left-4 right-4 bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-200 px-4 py-2 rounded-lg shadow-lg z-50">
        {audioError}
      </div>
    );
  };

  // Add console.log before return
  // console.log('Quran - Rendering with state:', {
  //   showNavigation,
  //   showSettings,
  //   isSettingsOpen,
  //   selectedSurah: quranData.selectedSurah,
  //   currentVerse: quranData.currentVerse
  // });

  // Preload fonts for visible and upcoming verses
  useEffect(() => {
    if (!quranData.surahContent) return;

    // Get unique page numbers for current and next few verses
    const currentIndex = quranData.currentVerse;
    const pagesToLoad = quranData.surahContent
      .slice(currentIndex, currentIndex + 10) // Load next 10 verses' fonts
      .map(verse => verse.page_number)
      .filter((value, index, self) => self.indexOf(value) === index); // Remove duplicates

    loadFontsForPages(pagesToLoad);
  }, [quranData.surahContent, quranData.currentVerse]);

  // Add effect to handle initial surah selection from location state
  useEffect(() => {
    if (location.state?.surahId && isSettingsInitialized) {
      // Remove console.log
      // console.log('Loading surah from location state:', location.state.surahId);
      setShowNavigation(false);
      quranData.setSelectedSurah(location.state.surahId);
      quranData.loadSurahContent(location.state.surahId);
    }
  }, [location.state?.surahId, isSettingsInitialized]);

  // Update auto-scroll effect to run only once per surah load
  useEffect(() => {
    // Skip if auto-scroll has already been completed or we're ignoring progress updates
    if (initialAutoScrollCompleteRef.current || ignoreReadingProgressUpdatesRef.current) return;
    
    // Only proceed if we're not loading and have content to work with
    if (!quranData.loading && quranData.surahContent?.length > 0 && quranData.selectedSurah) {
      console.log('🔍 Checking for last read verse in surah', quranData.selectedSurah);
      
      const surahHistory = settings.quran?.readingProgress?.history?.[quranData.selectedSurah] || {};
      const verseNumbers = Object.keys(surahHistory)
        .map(num => parseInt(num))
        .filter(num => !isNaN(num));
      
      if (verseNumbers.length > 0) {
        const lastReadVerseNum = Math.max(...verseNumbers);
        const verseIndex = quranData.surahContent.findIndex(
          verse => verse.verse_key === `${quranData.selectedSurah}:${lastReadVerseNum}`
        );
        
        console.log('📍 Last read verse found:', `${quranData.selectedSurah}:${lastReadVerseNum}`, 'at index:', verseIndex);
        
        if (verseIndex !== -1) {
          console.log('🎯 Preparing auto-scroll to verse index:', verseIndex);
          
          // Set flag to prevent duplicate scroll attempts during the timeout
          ignoreReadingProgressUpdatesRef.current = true;
          
          // Add a small delay to ensure the content is rendered
          setTimeout(() => {
            console.log('⏲️ Executing delayed auto-scroll to verse:', verseIndex);
            
            // Execute the scroll
            handleVerseScroll(verseIndex, {
              immediate: true,
              onSuccess: () => {
                console.log('✅ Initial auto-scroll complete');
                // Set the flag AFTER successful scroll
                initialAutoScrollCompleteRef.current = true;
                // Re-enable reading progress updates after a delay
                setTimeout(() => {
                  ignoreReadingProgressUpdatesRef.current = false;
                }, 2000);
              },
              onError: () => {
                console.log('❌ Initial auto-scroll failed');
                // Re-enable reading progress updates if scroll fails
                ignoreReadingProgressUpdatesRef.current = false;
              }
            });
          }, 500);
        } else {
          // No valid verse found, mark as complete to avoid further attempts
          initialAutoScrollCompleteRef.current = true;
        }
      } else {
        // No reading history, mark as complete to avoid further attempts
        initialAutoScrollCompleteRef.current = true;
      }
    }
  }, [quranData.loading, quranData.surahContent, quranData.selectedSurah, settings.quran?.readingProgress?.history, handleVerseScroll]);

  // Add effect to handle navigation visibility after settings are initialized
  useEffect(() => {
    if (isSettingsInitialized) {
      const hasLastReadingSession = settings?.quran?.lastReadingSession?.surahId;
      const hasSelectedSurah = quranData.selectedSurah;
      
      // Show navigation if there's no last reading session AND no selected surah
      if (!hasLastReadingSession && !hasSelectedSurah) {
        setShowNavigation(true);
      }
    }
  }, [isSettingsInitialized, settings?.quran?.lastReadingSession, quranData.selectedSurah]);

  // Ensure selectedTranslation is set on first load if not set
  useEffect(() => {
    if (
      quranData.translations &&
      quranData.translations.length > 0 &&
      quranData.selectedLanguage &&
      !quranData.selectedTranslation
    ) {
      const selectedLangName = quranData.languages.find(lang => lang.iso_code === quranData.selectedLanguage)?.name;
      const defaultTranslation = quranData.translations.find(
        t => t.language_name?.toLowerCase() === selectedLangName?.toLowerCase()
      );
      if (defaultTranslation) {
        updateSettings({
          ...settings,
          quran: {
            ...settings.quran,
            selectedTranslation: defaultTranslation.id.toString()
          }
        });
      }
    }
  }, [quranData.translations, quranData.selectedLanguage, quranData.selectedTranslation, quranData.languages, updateSettings, settings]);

  // Add this after the Quran component's main hooks and before the render logic
  useEffect(() => {
    function handleNativeVerseChanged(e) {
      const { verseIndex } = e.detail || {};
      // Only update if the surah and reciter match (to avoid jumping on navigation)
      if (
        quranAudio.isPlaying &&
        typeof verseIndex === 'number' &&
        quranData.selectedSurah &&
        quranData.selectedReciter
      ) {
        // Only update if verse is different
        if (quranData.currentVerse !== verseIndex) {
          quranData.setCurrentVerse(verseIndex);
          handleVerseScroll(verseIndex, { immediate: false });
        }
      }
    }
    window.addEventListener('quranAudio-verseChanged', handleNativeVerseChanged);
    return () => {
      window.removeEventListener('quranAudio-verseChanged', handleNativeVerseChanged);
    };
  }, [quranAudio.isPlaying, quranData.selectedSurah, quranData.selectedReciter, quranData.currentVerse, quranData.setCurrentVerse, handleVerseScroll]);

  // Keep lastReadingSession in sync with current surah/verse
  useEffect(() => {
    if (!isSettingsInitialized || !quranData.selectedSurah || !quranData.surahContent) return;
    const verseObj = quranData.surahContent[quranData.currentVerse];
    if (!verseObj) return;
    const surahId = quranData.selectedSurah;
    const verseId = verseObj.verse_key;
    // Only update if changed
    if (
      settings.quran?.lastReadingSession?.surahId !== surahId ||
      settings.quran?.lastReadingSession?.verseId !== verseId
    ) {
      updateSettings({
        ...settings,
        quran: {
          ...settings.quran,
          lastReadingSession: {
            surahId,
            verseId,
            timestamp: Date.now()
          }
        }
      });
    }
  }, [isSettingsInitialized, quranData.selectedSurah, quranData.currentVerse, quranData.surahContent]);

  // Force sync with native audio state on mount and on navigation to /quran
  useEffect(() => {
    if (location.pathname === '/quran' && typeof quranAudio.syncWithNative === 'function') {
      quranAudio.syncWithNative();
    }
  }, [location.pathname, quranAudio]);

  // Add effect to auto-scroll when audio is playing and the verse changes
  useEffect(() => {
    if (!quranAudio.isPlaying) return;
    if (quranData.currentVerse == null) return;
    // Only scroll if the verse changes while audio is playing
    handleVerseScroll(quranData.currentVerse, { immediate: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quranAudio.isPlaying, quranData.currentVerse]);

  // Main render
  if (!isSettingsInitialized) {
    return <LoadingState isDarkMode={isDarkMode} />;
  }
  return (
    <div className="h-full">
      {isMobileOrTabletPortrait ? renderMobileView() : renderDesktopView()}
      {renderModals()}
      {renderErrorMessage()}
    </div>
  );
}

export default React.memo(Quran);